import { Icon } from './Icon.js';

import './FieldLabel.css';

export type FieldKind = 'string' | 'number' | 'boolean' | 'enum';

interface Props {
  htmlFor?: string;
  label: string;
  required?: boolean;
  description?: string | undefined;
  error?: string | undefined;
  /**
   * Optional type-of-field hint. Renders a small colored badge before the
   * label so users can distinguish field types at a glance — useful when
   * the input itself doesn't make the type obvious (e.g. enum vs string).
   */
  kind?: FieldKind | undefined;
}

const KIND_BADGE: Record<FieldKind, { label: string; title: string }> = {
  string: { label: 'Aa', title: 'Text field' },
  number: { label: '#', title: 'Number field' },
  boolean: { label: 'T/F', title: 'True/false toggle' },
  enum: { label: '⌄', title: 'Choice from a fixed set' },
};

export function FieldLabel({
  htmlFor,
  label,
  required,
  description,
  error,
  kind,
}: Props): JSX.Element {
  const badge = kind ? KIND_BADGE[kind] : undefined;
  return (
    <div className="field-label">
      <label htmlFor={htmlFor} className="field-label-text">
        {badge && (
          <span
            className={`field-label-kind field-label-kind-${kind}`}
            aria-label={badge.title}
            title={badge.title}
          >
            {badge.label}
          </span>
        )}
        <span className="field-label-name">{label}</span>
        {required && (
          <span className="field-label-required" aria-label="required">
            ●
          </span>
        )}
      </label>
      {description && <p className="field-label-description">{description}</p>}
      {error && (
        <p className="field-label-error" role="alert">
          <Icon
            name="alert-circle"
            size={14}
            strokeWidth={2.2}
            className="field-label-error-icon"
          />
          <span className="field-label-error-text">{error}</span>
        </p>
      )}
    </div>
  );
}
